import React from "react";
import {
  Box,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Button,
  Link,
  Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import { LabelIconsDetection } from "../detectionComponent/svgIcon/labelIconDetection";
import { CircleSVG } from "../mainViewComponent/Svg";
import { LabelIcons } from "../newDashboard/Svg/labelIcons";

interface FloorInfo {
  name: string;
  totalDevices: number;
  totalRooms: number;
  colorOrder: any;
  rooms: Room[];
  isUnit?: boolean;
  type?: string;
  value: string | number; // Make this required if possible
}


interface Room {
  name: string;
  devices: Device[];
  statusCount: number;
  colorOrder: any;
  handleGetSummaryData: (type: string, id: any) => void;
  keyValue: string | number; // Make it required if it should never be null
}

interface Device {
  name: string;
  status: any;
  statusCount: number;
  colorOrder: any;
}

export const colorIndexData: any = {
  5: "#000000",
  4: "#871014",
  3: "#FFEF5C",
  2: "#FFEF5C",
  1: "#98E565",
  0: "#FFFFFF",
};


const getColor = (order: any) =>
  order === "-Infinity" ? "#FFFFFF" : colorIndexData[order];

const RoomCard = (props: any) => {
  // Destructure the necessary properties from props
  const { statusCount, handleGetSummaryData, name, devices, colorOrder, keyValue } = props;

  const getDeviceImage = (provider?: string) => {

    if (provider === "Verkada") {
      return "/assets/images/verkadaDevice.png"; // Image for Verkada
    }
    return "/assets/images/ieqDevice.png"; // Image for other providers
  };

  const handleDeviceClickData = (location: any, keyValue: any, device: any) => {
    handleGetSummaryData(location, keyValue)
  }

  return (
    <Card
      sx={{
        borderRadius: "10px!important",
        height: "100%",
        boxShadow: "none",
        border: "1px solid #dcdce1",
        p: 0,
      }}
    >
      <CardContent sx={{ p: "0 0 7px 0!important" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            p: "20px",
            borderRadius: "10px 10px 0px 0px",
            mb: "7px",
            backgroundColor: "#F6FBFF",
            cursor: statusCount > 0 ? "pointer" : "default",
          }}
          onClick={() => statusCount > 0 && handleGetSummaryData("loc", keyValue)}
        >
          <Box display="flex" alignItems="center" gap="10px">
            <Box sx={{ lineHeight: 0 }}>
              <LabelIconsDetection type="R" />
            </Box>
            {statusCount > 0 ? (
              <Link
                underline="hover"
                sx={{ textDecoration: 'none', color: "#32405A" }}
              >
                <Typography
                  variant="h6"
                  fontWeight={500}
                  fontSize="14px"
                  textTransform="capitalize"
                  color="#32405A"
                >
                  {name}
                </Typography>
              </Link>
            ) : (
              <Typography
                variant="h6"
                fontWeight={500}
                fontSize="14px"
                color="#32405A"
                textTransform="capitalize"
              >
                {name}
              </Typography>
            )}
          </Box>
          {statusCount > 0 && (
            <Box sx={{ display: "inline-block" }}>
              <Tooltip sx={{ zIndex: 9999 }} disableInteractive={false}
                arrow
                title={
                  <Box
                    sx={{
                      borderRadius: 1.5, // Smooth rounded corners
                      overflow: 'hidden',
                      minWidth: 180, // Ensures consistent width
                      boxShadow: 4, // Adds depth for a modern look
                    }}
                  >
                    {/* Header Section (Blue Background) */}
                    <Box sx={{ bgcolor: '#2563EB', px: 2, py: 1.2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="white">
                        Aggregate Summary
                      </Typography>
                    </Box>

                    {/* Value Section (White Background) */}
                    <Box sx={{ bgcolor: '#FFFFFF', px: 2, py: 1.2 }}>
                      <Typography variant="body2" fontWeight="500" color="#000000">
                        {`Room:  ${name}`}
                      </Typography>
                    </Box>
                  </Box>
                }
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'transparent', // Removes default tooltip background
                      p: 0, // Ensures custom padding is applied
                      boxShadow: 0, // Prevents double shadows
                    },
                  },
                  arrow: {
                    sx: {
                      color: '#2563EB', // Arrow color matching the header background
                    },
                  },
                }}
              >
                <span>
                  <CircleSVG
                    getnumber={`${statusCount}`}
                    bgcolor={getColor(colorOrder)}
                    fontColor=""
                  />
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
        {devices && devices.length > 0 ? (
          devices.map((device: any, index: number) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ p: "7px 20px" }}
            >
              <Box display="flex" alignItems="center" gap="10px">
                <Box sx={{ lineHeight: 0 }}>
                  <img src={`/assets/images/DeviceIcon.svg`} alt="device" />
                </Box>
                <Typography
                  variant="body2"
                  fontWeight="normal"
                  fontSize="13px"
                  color="#32405A"
                  textTransform="capitalize"
                >
                  {device.name}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }} onClick={() => handleDeviceClickData("loc", keyValue, device)}>
                <Box sx={{ pt: 1 }}>
                  <Tooltip
                    arrow
                    title={
                      <Box
                        sx={{
                          borderRadius: 1.5, // Smooth rounded corners
                          overflow: 'hidden',
                          minWidth: 90, // Ensures consistent width
                          boxShadow: 4, // Adds depth for a modern look
                        }}
                      >
                        {/* Header Section (Blue Background) */}
                        <Box sx={{ bgcolor: '#2563EB', px: 2, py: 1.2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="white">
                            {device?.provider ? device?.provider : "IEQ Device"}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'transparent', // Removes default tooltip background
                          p: 0, // Ensures custom padding is applied
                          boxShadow: 0, // Prevents double shadows
                        },
                      },
                      arrow: {
                        sx: {
                          color: '#2563EB', // Arrow color matching the header background
                        },
                      },
                    }}
                  >
                    <span>
                      <img src={getDeviceImage(device?.provider)} width={25} height={25} alt="device" />
                    </span>
                  </Tooltip>
                </Box>
                <Box sx={{ cursor: "pointer", pt: 1 }}>
                  <Tooltip
                    arrow
                    title={
                      <Box
                        sx={{
                          borderRadius: 1.5, // Smooth rounded corners
                          overflow: 'hidden',
                          minWidth: 180, // Ensures consistent width
                          boxShadow: 4, // Adds depth for a modern look
                        }}
                      >
                        {/* Header Section (Blue Background) */}
                        <Box sx={{ bgcolor: '#2563EB', px: 2, py: 1.2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="white">
                            Device
                          </Typography>
                        </Box>
                        <Box sx={{ bgcolor: '#FFFFFF', px: 2, py: 1.2 }}>
                          <Typography variant="body2" fontWeight="500" color="#000000">
                            {device.name}
                          </Typography>
                        </Box>

                      </Box>
                    }
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'transparent', // Removes default tooltip background
                          p: 0, // Ensures custom padding is applied
                          boxShadow: 0, // Prevents double shadows
                        },
                      },
                      arrow: {
                        sx: {
                          color: '#2563EB', // Arrow color matching the header background
                        },
                      },
                    }}
                  >
                    <span>
                      <CircleSVG
                        getnumber=""
                        bgcolor={getColor(device.colorOrder)}
                        fontColor=""
                      />
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" align="center">
            No device found
          </Typography>
        )}
      </CardContent>

    </Card>
  );
};


interface FloorInfoItemProps {
  icon: string;
  title: string;
  value: number;
  type?: any;
  status?: any;
  handleGetSummaryData: (type: string, id: any) => void;
  itemId: any;
}

const renderIcon = (type: any, icon: string) => {
  if (type === "room") return <LabelIconsDetection type="R" />;
  if (type === "unit") return <LabelIcons type="L" isUnit={true} />;
  if (type === "FU") return <LabelIcons type="FU" />;
  return <img src={`/assets/images/${icon}.svg`} alt={icon} />;
};

const FloorInfoItem: React.FC<FloorInfoItemProps> = ({
  icon,
  title,
  value,
  status,
  type,
  handleGetSummaryData,
  itemId,
}) => {
  // Determine if the item should be clickable (and styled as such)
  const isClickable = value > 0 && type !== "room" && type !== "device";
  const cursorStyle = (value > 0 && type !== "room" && type !== "device") ? "pointer" : "default";

  // Render the title either as a clickable Link or plain Typography
  const renderTitle = () => {
    const titleComponent = (
      <Typography
        variant="h6"
        fontWeight={500}
        fontSize="14px"
        textTransform="capitalize"
        color="#32405A"
      >
        {title}
      </Typography>
    );
    return isClickable ? (
      <Link underline="hover" sx={{ textDecoration: 'none', color: "#32405A" }}>
        {titleComponent}
      </Link>
    ) : (
      titleComponent
    );
  };

  // Render the circle either using CircleSVG (with background color) or a plain styled div
  const renderCircle = () => {
    if (value <= 0) return null;
    return isClickable ? (
      <Tooltip
        arrow
        title={
          <Box
            sx={{
              borderRadius: 1.5, // Smooth rounded corners
              overflow: 'hidden',
              minWidth: 180, // Ensures consistent width
              boxShadow: 4, // Adds depth for a modern look
            }}
          >
            {/* Header Section (Blue Background) */}
            <Box sx={{ bgcolor: '#2563EB', px: 2, py: 1.2 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="white">
                Aggregate Summary
              </Typography>
            </Box>

            {/* Value Section (White Background) */}
            <Box sx={{ bgcolor: '#FFFFFF', px: 2, py: 1.2 }}>
              <Typography variant="body2" fontWeight="500" color="#000000">
                {`Floor : ${title}`}
              </Typography>
            </Box>
          </Box>
        }
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'transparent', // Removes default tooltip background
              p: 0, // Ensures custom padding is applied
              boxShadow: 0, // Prevents double shadows
            },
          },
          arrow: {
            sx: {
              color: '#2563EB', // Arrow color matching the header background
            },
          },
        }}
      >
        <span> {/* Ensures Tooltip has a valid wrapper */}
          <CircleSVG getnumber={`${value}`} bgcolor={getColor(status)} fontColor="" />
        </span>
      </Tooltip>
    ) : (
      <div
        style={{
          width: "27px",
          height: "27px",
          borderRadius: "50%",
          border: "2px solid #d6d6d6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "12px",
          fontWeight: "bold",
          color: "black",
        }}
      >
        {value}
      </div>
    );
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="10px"
      justifyContent="space-between"
      sx={{ cursor: cursorStyle }}
      onClick={() => isClickable && handleGetSummaryData("flr", itemId)}
    >
      <Box display="flex" alignItems="center" gap="10px">
        <Box sx={{ lineHeight: 0 }}>
          {renderIcon(type, icon)}
        </Box>
        {renderTitle()}
      </Box>
      {renderCircle()}
    </Box>
  );
};

interface FacilityAccordionProps {
  floor: FloorInfo;
  index: number;
  expanded: string | false;
  onChange: (panel: string) => void;
  handleGetSummaryData: (type: string, id: any) => void;
}

export const FacilityAccordion: React.FC<FacilityAccordionProps> = ({
  floor,
  index,
  expanded,
  onChange,
  handleGetSummaryData,
}) => {
  const panelName = `panel${index}`;
  console.log("FacilityAccordion ::", floor);

  return (
    <Accordion
      expanded={expanded === panelName}
      onChange={() => onChange(panelName)}
      sx={{
        borderRadius: "10px !important",
        boxShadow: "none",
        border: "1px solid #dcdce1",
      }}
    >
      <AccordionSummary
        sx={{
          backgroundColor: "#F6FBFF",
          p: { xs: "10px", sm: "20px" },
          borderRadius: "10px",
          "& .MuiAccordionSummary-expandIconWrapper": {
            ml: { xs: "5px", sm: "10px" },
          },
          "& .MuiAccordionSummary-content": {
            m: 0,
          },
        }}
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index}-content`}
        id={`panel${index}-header`}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={{ xs: 2, sm: "30px" }}
          sx={{
            width: "100%",
            justifyContent: { xs: "flex-start", sm: "space-between" },
          }}
        >
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems="center"
            gap={{ xs: 1, sm: "30px" }}
          >
            <FloorInfoItem
              icon="FloorIcon"
              type={floor.isUnit ? "unit" : floor.type || ""}
              title={floor.name}
              value={floor.totalDevices}
              status={floor.colorOrder}
              handleGetSummaryData={handleGetSummaryData}
              itemId={floor.value}
            />
            <FloorInfoItem
              icon="Group"
              type="room"
              title="Number of rooms"
              value={floor.totalRooms}
              status={floor.colorOrder}
              handleGetSummaryData={handleGetSummaryData}
              itemId={floor.value}
            />
            <FloorInfoItem
              icon="DeviceIcon"
              type="device"
              title="Number of Devices"
              value={floor.totalDevices}
              status={floor.colorOrder}
              handleGetSummaryData={handleGetSummaryData}
              itemId={floor.value}
            />
          </Box>
          {/* <Button
            variant="contained"
            color="primary"
            onClick={handleOverviewClick}
            sx={{ mt: { xs: 2, sm: 0 } }}
          >
            <MenuIcon sx={{ mr: { xs: "5px", sm: "10px" } }} />
            Overview
          </Button> */}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: "10px" }}>
        {floor.rooms && floor.rooms.length > 0 ? (
          <Grid container spacing={2} alignItems="stretch">
            {floor.rooms.map((room: any, roomIndex) => (
              <Grid item xs={12} md={3} key={roomIndex}>
                <RoomCard
                  {...room}
                  keyValue={room?.key}
                  handleGetSummaryData={handleGetSummaryData}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" align="center">
            No room found
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
